import { Box, useBreakpointValue, useToken } from '@chakra-ui/react';
import {
  CardCarouselButton,
  CarouselContent,
  CloudinaryVideo,
  Grid,
  MotionBox,
  useCarousel,
} from '@time-line/components/common';
import type { AspectRatio } from '@time-line/components/types';
import { ratioToDecimal } from '@time-line/components/utils';
import { AnimatePresence } from 'motion/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWindowSize } from 'react-use';
import type { SocialMediaCarousel as SocialMediaCarouselProps } from 'types/cms/blocks';

import { ANIMATION_DURATION } from './constants';
import { SocialMediaCarouselOverlay } from './SocialMediaCarouselOverlay';

/* INFO: These dims are pulled from Figma */
const CARD_WIDTH = {
  BASE: 235,
  EXPANDED: 318,
};

const CARD_HEIGHT = {
  BASE: 352,
  EXPANDED: 477,
};

const SCALE_FACTOR = CARD_WIDTH.EXPANDED / CARD_WIDTH.BASE;
const EXTRA_WIDTH = CARD_WIDTH.BASE * (SCALE_FACTOR - 1);
const PADDING_TOP = CARD_HEIGHT.EXPANDED - CARD_HEIGHT.BASE;
const ADJACENT_MARGIN = EXTRA_WIDTH / 2;
const ASPECT: AspectRatio = `${CARD_WIDTH.BASE}:${CARD_HEIGHT.BASE}`;
const ASPECT_DECIMAL = ratioToDecimal(ASPECT);

const variants = {
  open: ({ translateX = 0, scale = 1 }) => ({
    opacity: 1,
    scale,
    x: translateX,
    transition: {
      duration: ANIMATION_DURATION,
    },
  }),
  closed: ({ translateX = 0 }) => ({
    opacity: 0.6,
    scale: 1,
    x: translateX,
    transition: {
      duration: ANIMATION_DURATION,
    },
  }),
};

interface SocialMediaCarouselItemsProps extends Pick<
  SocialMediaCarouselProps,
  'items'
> {
  selectedItemIdx: number;
  setSelectedItemIdx: (index: number) => void;
}

export const SocialMediaCarouselItems = ({
  selectedItemIdx,
  setSelectedItemIdx,
  items,
}: SocialMediaCarouselItemsProps) => {
  const { realIndex, showControls, slideTo } = useCarousel();
  const [muted, setMuted] = useState(true);
  const volumeButtonSpace = useToken('space', '3xs');
  const { update } = useCarousel();
  const { width } = useWindowSize();
  const clampedWidth = useMemo(
    () =>
      Math.ceil(width / (CARD_WIDTH.BASE + ADJACENT_MARGIN)) *
      (CARD_WIDTH.BASE + ADJACENT_MARGIN),
    [width],
  );

  const isMobile = useBreakpointValue(
    { base: true, md: false },
    { fallback: 'md' },
  );

  const spaceBetween = useBreakpointValue(
    { base: 12, md: 16 },
    { fallback: 'md' },
  );

  const paddingTop = useBreakpointValue(
    { base: 0, md: PADDING_TOP },
    { fallback: 'md' },
  ) as number;

  const handleSlideChange = useCallback(
    (index: number) => {
      setSelectedItemIdx(index);
      slideTo(index);
    },
    [setSelectedItemIdx, slideTo],
  );

  useEffect(() => {
    if (realIndex !== undefined) setSelectedItemIdx(realIndex);
  }, [realIndex, setSelectedItemIdx]);

  /* INFO: Add duplicate slides if widescreen. The custom layout in this component
   * looks janky if there are not enough slides to fill the width of the screen.
   * Repeat the full items array as many times as needed so the loop has no
   * visible duplication seam. The duplication runs in an effect (not useMemo)
   * so the SSR and first client render produce identical DOM — useWindowSize
   * defaults to Infinity on the server, and updating in render would cause a
   * hydration mismatch. */
  const [additionalItems, setAdditionalItems] = useState(items);
  useEffect(() => {
    if (items.length === 0) {
      setAdditionalItems(items);
      return;
    }
    if (!Number.isFinite(clampedWidth) || clampedWidth <= 0) {
      setAdditionalItems(items);
      return;
    }
    const approxSlidesNeeded = Math.ceil(
      (clampedWidth * 2) / (CARD_WIDTH.BASE + ADJACENT_MARGIN),
    );
    if (items.length >= approxSlidesNeeded) {
      setAdditionalItems(items);
      return;
    }
    const repetitions = Math.ceil(approxSlidesNeeded / items.length);
    setAdditionalItems(Array.from({ length: repetitions }, () => items).flat());
  }, [items, clampedWidth]);

  useEffect(() => {
    update();
  }, [additionalItems, update]);

  return (
    <Grid
      maxW="100%"
      gridColumn="full"
      overflowX="hidden"
      position="relative"
      pt={`${paddingTop}px`}
      _hover={{
        'button:not([disabled])': {
          opacity: 1,
        },
      }}
      sx={{
        '.swiper-horizontal': {
          overflow: 'visible',
          display: 'grid',
          gridTemplateColumns: 'subgrid',
          gridColumn: 'full',
          width: '100%',
        },
        '.swiper-slide': {
          width: 'auto',
        },
      }}
    >
      <CarouselContent
        slidesPerView="auto"
        centeredSlides
        centerInsufficientSlides
        spaceBetween={spaceBetween}
        loop
        slideToClickedSlide={false}
      >
        {additionalItems.map((item, index) => {
          const isSelected = selectedItemIdx === index;

          const getTranslateX = () => {
            if (!isMobile) {
              const getCircularDistance = (from: number, to: number) => {
                const forward =
                  (to - from + additionalItems.length) % additionalItems.length;
                const backward =
                  (from - to + additionalItems.length) % additionalItems.length;
                /* Return shortest dist */
                return forward <= backward ? forward : -backward;
              };

              const distance = getCircularDistance(selectedItemIdx, index);
              const isLeft = distance < 0;
              const isRight = distance > 0;

              if (!isSelected) {
                if (isLeft) return -ADJACENT_MARGIN;
                if (isRight) return ADJACENT_MARGIN;
              }
            }
            return 0;
          };

          return (
            <>
              <MotionBox
                key={index}
                cursor="pointer"
                onClick={() => handleSlideChange(index)}
                initial={isSelected ? 'open' : 'closed'}
                animate={selectedItemIdx === index ? 'open' : 'closed'}
                variants={variants}
                custom={{
                  translateX: getTranslateX(),
                  scale: isMobile ? 1 : SCALE_FACTOR,
                }}
                width={CARD_WIDTH.BASE}
                aspectRatio={ASPECT_DECIMAL}
                transformOrigin="bottom"
                borderRadius="base"
                overflow="hidden"
                whileHover={{
                  opacity: 1,
                }}
                transition={{
                  opacity: { duration: 0.2, ease: 'easeInOut' },
                }}
              >
                <CloudinaryVideo
                  cloudinaryId={item.video.cloudinaryId}
                  title={item.video.title}
                  canPlay={isSelected}
                  poster={item.poster}
                  aspectRatio={ASPECT}
                  controls={isMobile}
                  playButton={false}
                  externalVolumeControl
                  muted={muted}
                  onMutedChange={setMuted}
                />
              </MotionBox>
              <AnimatePresence>
                {isSelected && (
                  <SocialMediaCarouselOverlay
                    key={index}
                    scaleFactor={isMobile ? 1 : SCALE_FACTOR}
                    volumeButtonProps={{
                      muted,
                      onToggleMuted: () => setMuted(!muted),
                      top: `calc(${-paddingTop}px + ${volumeButtonSpace})`,
                      right: `calc(${isMobile ? 0 : -ADJACENT_MARGIN}px + ${volumeButtonSpace})`,
                    }}
                    {...item}
                  />
                )}
              </AnimatePresence>
            </>
          );
        })}
      </CarouselContent>
      {showControls && (
        <Box
          gridColumn="main"
          position="absolute"
          width="100%"
          top={`calc(50% + ${paddingTop / 2}px)`}
        >
          <CardCarouselButton direction="prev" zIndex={1} />
          <CardCarouselButton direction="next" zIndex={1} />
        </Box>
      )}
    </Grid>
  );
};
